import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../shared/api/client";
import type { PageResponse, ProjectSummary } from "../../shared/types/api";

const STORAGE_KEY = "taskmgmt-last-project";

/**
 * Which project the backlog and sprint screens are showing.
 *
 * <p>Those screens are project-scoped but sit at the top of the §14 navigation, so they need a
 * project before they can show anything. The choice is remembered because a delivery team works
 * on one project for weeks at a time, and re-picking it on every visit is friction with no
 * purpose.
 *
 * <p>Reading and writing are wrapped: a private window, cleared site data or a browser set to
 * block storage all make this throw, and none of them is a reason for the page to fail.
 */
export function useProjectSelection() {
  const [projectId, setProjectId] = useState<string>(() => remembered() ?? "");

  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
  });

  const available = useMemo(() => projects.data?.content ?? [], [projects.data]);

  useEffect(() => {
    const first = available[0];
    if (!first) {
      return;
    }
    // The remembered project may have been archived, or may belong to an account this person no
    // longer has access to. Falling back to the first available one beats an empty screen with
    // no explanation of why it is empty.
    if (!available.some((project) => project.id === projectId)) {
      select(first.id);
    }
  }, [available, projectId]);

  const select = (id: string) => {
    setProjectId(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Storage unavailable. The choice simply will not be remembered next time.
    }
  };

  return {
    projectId,
    select,
    projects: available,
    loading: projects.isPending,
    error: projects.isError ? projects.error : null,
    selected: available.find((project) => project.id === projectId) ?? null,
  };
}

function remembered(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
